"use client";

import { useMemo, useState } from "react";
import { Users } from "lucide-react";

import { useGetAgentStats } from "../../hooks/use-hook";
import type { AgencyUserWithAgency, AgentStats } from "../../types/types";

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

// Talk time comes back in seconds.
const talk = (s = 0) => {
  if (s <= 0) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m` : "<1m";
};

const SHOWN = 8;

/** The agency-user shape AgentDetailsView opens with; it refetches everything else by userId/agencyId. */
export const agentStatsToAgencyUser = (agent: AgentStats, fallbackAgencyId: string): AgencyUserWithAgency => {
  const now = new Date().toISOString();
  const agencyId = agent.agencyId || fallbackAgencyId;
  return {
    id: agent.id,
    agencyId,
    userId: agent.id, // AgentStats.id is the user id
    role: "agent",
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    user: { id: agent.id, name: agent.name, email: agent.email || "", title: agent.specialization || "", isOnline: agent.isOnline },
    agency: { id: agencyId, name: agent.agencyName || "" },
    agencyName: agent.agencyName,
  } as unknown as AgencyUserWithAgency;
};

/**
 * The agents behind the numbers, across every level below this agency, ranked by
 * production and then talk time — so someone dialing all week without a sale still
 * sits above an idle seat. Upline owners are already dropped server-side.
 */
export function AgencyAgentsPanel({
  agencyId,
  period = "6-months",
  startDate,
  endDate,
  onViewAgentDetails,
}: {
  agencyId: string;
  period?: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
  /** Opens the agent's performance page (AgentDetailsView). Rows are clickable when set. */
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void;
}) {
  const { data, isLoading, isError } = useGetAgentStats(agencyId, period, startDate, endDate);
  const [showAll, setShowAll] = useState(false);

  const agents = useMemo(
    () =>
      [...((data as AgentStats[] | undefined) ?? [])].sort(
        (a, b) =>
          (b.totalPremium || 0) - (a.totalPremium || 0) ||
          (b.dialTime || 0) - (a.dialTime || 0) ||
          (a.name || "").localeCompare(b.name || ""),
      ),
    [data],
  );
  const producing = agents.filter((a) => (a.totalPremium || 0) > 0).length;
  const rows = showAll ? agents : agents.slice(0, SHOWN);

  return (
    // min-w-0: without it the table's width stretches the grid column instead of scrolling inside.
    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-sky-50 text-sky-600">
            <Users className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-800">Agents</p>
            <p className="text-[11px] text-muted-foreground">Who&apos;s producing and who&apos;s putting in the time</p>
          </div>
        </div>
        {agents.length > 0 && (
          <div className="text-right">
            <p className="text-xl font-bold tabular-nums tracking-tight text-sky-600">
              {producing} of {agents.length}
            </p>
            <p className="text-[11px] text-muted-foreground">producing</p>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-7 animate-pulse rounded bg-gray-100" />
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Couldn&apos;t load this agency&apos;s agents.</p>
      ) : agents.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No agents in this agency yet.</p>
      ) : (
        <>
          {/* "Show all" on a big agency scrolls in here; the column labels stay pinned. */}
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-[1]">
                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground [&>th]:bg-white [&>th]:shadow-[inset_0_-1px_0_#e5e7eb]">
                  <th className="pb-1.5 text-left font-medium">Agent</th>
                  <th className="pb-1.5 text-right font-medium" title="Everything reported: sold & approved plus underwriting">
                    Production
                  </th>
                  <th className="pb-1.5 text-right font-medium" title="Sold & approved only">
                    Issued
                  </th>
                  <th className="pb-1.5 text-right font-medium">Policies</th>
                  <th className="pb-1.5 text-right font-medium">Close</th>
                  <th className="pb-1.5 text-right font-medium">Talk time</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr
                    key={a.userId || a.id}
                    // The name button below bubbles here, so mouse and keyboard both open it once.
                    onClick={onViewAgentDetails ? () => onViewAgentDetails(agentStatsToAgencyUser(a, agencyId)) : undefined}
                    className={`border-b last:border-0 hover:bg-gray-50 ${onViewAgentDetails ? "cursor-pointer" : ""}`}
                  >
                    <td className="max-w-[180px] py-2 pr-2">
                      <div className="flex items-center gap-1.5">
                        <span
                          role="img"
                          aria-label={a.isOnline ? "Online" : "Offline"}
                          title={a.isOnline ? "Online" : "Offline"}
                          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${a.isOnline ? "bg-emerald-500" : "bg-gray-300"}`}
                        />
                        {onViewAgentDetails ? (
                          <button
                            type="button"
                            title="Open performance"
                            className="truncate rounded-sm text-left text-gray-700 hover:text-primary hover:underline focus-visible:!outline-solid focus-visible:!outline-2 focus-visible:outline-offset-2 focus-visible:!outline-primary"
                          >
                            {a.name || "Unnamed"}
                          </button>
                        ) : (
                          <span className="truncate text-gray-700">{a.name || "Unnamed"}</span>
                        )}
                      </div>
                      {a.agencyId && a.agencyId !== agencyId && a.agencyName && (
                        <p className="truncate pl-3 text-[10px] text-muted-foreground">{a.agencyName}</p>
                      )}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums text-gray-900">
                      {a.totalPremium ? money(a.totalPremium) : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums text-emerald-600">
                      {a.totalPremium ? money(a.issuedPremium || 0) : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-900">{(a.totalPolicies || 0).toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{(a.closingRate || 0).toFixed(1)}%</td>
                    <td className="py-2 text-right tabular-nums text-gray-900">{talk(a.dialTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {agents.length > SHOWN && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 w-full rounded-md py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
            >
              {showAll ? `Show top ${SHOWN}` : `Show all ${agents.length} agents`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
